import React, { useEffect } from "react";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import IconButton from "@mui/material/IconButton";
import { useParams, useNavigate } from "react-router-dom";
import { ReauthenticationBanner } from "../components/ReauthenticationBanner";
import { ReminderBanner } from "../components/ReminderBanner";
import { SyncResults } from "../components/SyncResults";
import { SyncSalesforceButton } from "../components/SyncSalesforceButton";
import { EditableIntegrationTitle } from "../components/EditableIntegrationTitle";
import { SalesforceIntegrationsDropdown } from "../components/SalesforceIntegrationsDropdown";
import { SalesforceSettingsModal } from "../components/settings/SalesforceSettingsModal";
import { useSnackbar } from "../../../components/Snackbar";
import { ProductFilter } from "../../../components/filters/ProductFilter/ProductFilter";
import { SpendCategoryInput } from "../../../components/filters/SpendCategory/SpendCategoryInput";
import { SELECTION_PRESETS } from "../../../components/filters/TreeSelect/hooks/useHierarchicalSelection";
import { DETAILS_PAGE_TEXTS } from "../consts";
import CreditsProgressBar from "../../../components/Credits/CreditsProgressBar";
import {
  WithSalesforceIntegrationDetailsContext,
  useSalesforceIntegrationDetailsContext,
} from "../contexts/useSalesforceIntegrationDetailsContext";
import { EnrichmentSynchronizationState } from "../types";
import {
  SalesforcePageContext,
  useSalesforcePageContextFactory,
} from "../contexts/useSalesforcePageContext";

const FilterSection: React.FC<{
  title: string;
  description: string;
  isUpdating: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
  gridSize?: { xs: number; md: number };
}> = ({
  title,
  description,
  isUpdating,
  isLoading = false,
  children,
  gridSize = { xs: 12, md: 6 },
}) => {
  return (
    <Grid size={gridSize}>
      <Paper
        elevation={0}
        sx={{ p: 2, border: 1, borderColor: "divider", height: "100%" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1">{title}</Typography>
          {isUpdating && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                {DETAILS_PAGE_TEXTS.generic.saving}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {isLoading ? (
          <Box>
            <Skeleton variant="rectangular" width="100%" height={40} />
          </Box>
        ) : (
          children
        )}
      </Paper>
    </Grid>
  );
};

const IntegrationEmptyState: React.FC<{
  icon?: React.ReactNode;
  title?: string;
  description?: string;
  buttonText?: string;
  onNavigate: () => void;
}> = ({
  icon,
  title = DETAILS_PAGE_TEXTS.emptyState.title,
  description = DETAILS_PAGE_TEXTS.emptyState.description,
  buttonText = DETAILS_PAGE_TEXTS.emptyState.buttonText,
  onNavigate,
}) => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={true} timeout={600}>
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "50vh" }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 6,
              maxWidth: 500,
              width: "100%",
              textAlign: "center",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Stack spacing={3} alignItems="center">
              {icon || (
                <SearchOffIcon
                  sx={{
                    fontSize: 72,
                    color: "text.secondary",
                    opacity: 0.5,
                  }}
                />
              )}
              <Stack spacing={1}>
                <Typography variant="h5" gutterBottom>
                  {title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {description}
                </Typography>
              </Stack>
              {buttonText && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={onNavigate}
                  sx={{ mt: 2 }}
                >
                  {buttonText}
                </Button>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Fade>
    </Container>
  );
};

// Content component that uses the context
const SalesforceIntegrationDetailsContent = () => {
  const navigate = useNavigate();
  const { showInfo } = useSnackbar();
  const {
    integration,
    integrationsLoading,
    integrationsError,
    productsSelection,
    categoriesSelection,
    showReauthenticationBanner,
    synchronizationState,
    handleProductsSelect,
    handleSpendCategoriesSelect,
    isSettingsModalOpen,
    setIsSettingsModalOpen,
  } = useSalesforceIntegrationDetailsContext();

  // Check if this is a new integration that needs initial setup
  const isInitialSetup =
    synchronizationState === EnrichmentSynchronizationState.NEVER_RAN_MATCHING;

  // Auto-open settings modal for new integrations
  useEffect(() => {
    // Only open if it's initial setup, integration exists, AND modal is not already open
    if (isInitialSetup && integration && !isSettingsModalOpen) {
      setIsSettingsModalOpen(true);
      showInfo(
        "Please configure your integration synchronization settings. This step is mandatory."
      );
    }
  }, [
    isInitialSetup,
    integration,
    isSettingsModalOpen,
    showInfo,
    setIsSettingsModalOpen,
  ]);

  // Settings modal handlers
  const handleOpenSettings = () => {
    setIsSettingsModalOpen(true);
  };

  if (integrationsError) {
    const isAuthorizationError = integrationsError.message?.includes(
      "Account not authorized"
    );

    return (
      <IntegrationEmptyState
        icon={
          isAuthorizationError ? (
            <LockOutlinedIcon
              sx={{
                fontSize: 72,
                color: "text.secondary",
                opacity: 0.5,
              }}
            />
          ) : (
            <ErrorOutlineIcon
              sx={{
                fontSize: 72,
                color: "error.main",
                opacity: 0.7,
              }}
            />
          )
        }
        title={
          isAuthorizationError
            ? DETAILS_PAGE_TEXTS.authorizationError.title
            : DETAILS_PAGE_TEXTS.integrationError.title
        }
        description={
          isAuthorizationError
            ? DETAILS_PAGE_TEXTS.authorizationError.description
            : DETAILS_PAGE_TEXTS.errors.errorWithMessage(
                integrationsError.message
              )
        }
        onNavigate={() => navigate("/integrations/salesforce")}
      />
    );
  }

  // Show empty state when integration is not found after loading
  if (!integrationsLoading && !integration) {
    return (
      <IntegrationEmptyState
        onNavigate={() => navigate("/integrations/salesforce")}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {integrationsLoading ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Skeleton variant="text" width={300} height={37} />
            <Skeleton variant="circular" width={37} height={37} />
          </Box>
          <Skeleton variant="rectangular" width={120} height={37} />
        </Stack>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          {integration && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditableIntegrationTitle />
              <SalesforceIntegrationsDropdown />
            </Box>
          )}
          {!!integration && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <CreditsProgressBar loadingVariant="skeleton" />
              <SyncSalesforceButton />
              <IconButton
                onClick={handleOpenSettings}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <SettingsOutlinedIcon />
              </IconButton>
            </Stack>
          )}
        </Stack>
      )}

      {/* Reauthentication Banner */}
      {showReauthenticationBanner && integration && (
        <Box sx={{ mb: 2 }}>
          <ReauthenticationBanner />
        </Box>
      )}

      {/* Sync Reminder Banner */}
      <ReminderBanner />

      {/* Sync Results Section */}
      <Box sx={{ mb: 3 }}>
        <SyncResults />
      </Box>

      {/* Product and Spend Category Filters */}
      <Grid container spacing={3}>
        <FilterSection
          title={DETAILS_PAGE_TEXTS.productSelection.label}
          description={DETAILS_PAGE_TEXTS.productSelection.description}
          isUpdating={productsSelection.updating}
          isLoading={productsSelection.loading}
          gridSize={{
            xs: 12,
            md:
              integration?.syncSettings?.exportItSpend || integrationsLoading
                ? 6
                : 12,
          }}
        >
          <ProductFilter
            onProductsSelect={handleProductsSelect}
            selectedProductIds={productsSelection.value}
            uploadProps={{ showSuccessMessage: false, noProductsLimit: true }}
          />
        </FilterSection>

        {(integration?.syncSettings?.exportItSpend || integrationsLoading) && (
          <FilterSection
            title={DETAILS_PAGE_TEXTS.spendCategories.label}
            description={DETAILS_PAGE_TEXTS.spendCategories.description}
            isUpdating={categoriesSelection.updating}
            isLoading={categoriesSelection.loading}
            gridSize={{ xs: 12, md: 6 }}
          >
            <SpendCategoryInput
              selectionRules={SELECTION_PRESETS.independent}
              selectedItems={categoriesSelection.value}
              onSpendCategorySelect={handleSpendCategoriesSelect}
            />
          </FilterSection>
        )}
      </Grid>

      {/* Settings Modal */}
      <SalesforceSettingsModal />
    </Container>
  );
};

const SalesforceIntegrationDetailsPageContent = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const pageContext = useSalesforcePageContextFactory();

  // Get the integration directly from pageContext
  const integration = pageContext.orgs?.find((org) => org.id === integrationId);

  if (pageContext.loading) {
    return <></>;
  }

  // Validate both integrationId and integration existence
  if (!integrationId || !integration?.id) {
    return (
      <IntegrationEmptyState
        onNavigate={() => navigate("/integrations/salesforce")}
      />
    );
  }

  return (
    <SalesforcePageContext.Provider value={pageContext}>
      <WithSalesforceIntegrationDetailsContext integration={integration}>
        <SalesforceIntegrationDetailsContent />
        <Backdrop
          sx={{
            backdropFilter: "blur(5px)",
            color: "#fff",
            zIndex: 1399,
            flexDirection: "column",
            gap: 2,
          }}
          open={pageContext.showBackdrop}
        >
          <CircularProgress color="inherit" size={60} />
        </Backdrop>
      </WithSalesforceIntegrationDetailsContext>
    </SalesforcePageContext.Provider>
  );
};

export const SalesforceIntegrationDetailsPage = () => {
  const { integrationId } = useParams<{ integrationId: string }>();

  return <SalesforceIntegrationDetailsPageContent key={integrationId} />;
};
